import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  TextField,
  Alert,
  AlertTitle,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { importFoods } from 'src/api/foods';

// ----------------------------------------------------------------------
// Template URLs - Update these with your Google Drive links
// ----------------------------------------------------------------------

const EASY_IMPORT_TEMPLATE_URL = 'https://docs.google.com/spreadsheets/d/14a9uLm3tXFZ5s7MEoC_ldbBF6BAmUwFSS7eBs6r3Rjs/edit?usp=sharing';
const COMPLEX_IMPORT_TEMPLATE_URL = 'https://docs.google.com/spreadsheets/d/1pkB6lJHtU6bRMLf5mxBUB_f0UkMvaTF7/edit?usp=sharing&ouid=118338309904783666284&rtpof=true&sd=true';

// ----------------------------------------------------------------------

type FoodImportModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  restaurantId: string;
};

export default function FoodImportModal({
  open,
  onClose,
  onSuccess,
  restaurantId,
}: FoodImportModalProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [importType, setImportType] = useState<'easy' | 'complex'>('easy');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<{
    created: number;
    updated: number;
    errors: Array<{ sheet?: string; row?: number; error: string }>;
  } | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setImportType('easy');
      setSelectedFile(null);
      setImportResult(null);
    }
  }, [open]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  }, []);

  const handleDownloadTemplate = (type: 'easy' | 'complex') => {
    const url = type === 'easy' ? EASY_IMPORT_TEMPLATE_URL : COMPLEX_IMPORT_TEMPLATE_URL;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      enqueueSnackbar('Please select a file to import', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setImportResult(null); // Clear previous results

      // Map 'easy' to 'simple' for API compatibility
      const mode = importType === 'easy' ? 'simple' : 'complex';

      // Call the import API
      const result = await importFoods(restaurantId, mode, selectedFile);

      // Store the result to display in modal
      setImportResult(result);

      // Check if there are errors
      if (result.errors && result.errors.length > 0) {
        // Partial success or complete failure - show result in modal
        enqueueSnackbar(
          `Import completed: ${result.created} created, ${result.updated} updated, ${result.errors.length} error(s). See details below.`,
          {
            variant: result.created > 0 || result.updated > 0 ? 'warning' : 'error',
          }
        );
        // Don't close modal so user can see the error details
      } else {
        // Full success
        enqueueSnackbar(
          `Import successful: ${result.created} foods created, ${result.updated} foods updated`,
          {
            variant: 'success',
          }
        );

        if (onSuccess) {
          onSuccess();
        }

        onClose();
      }
    } catch (error) {
      console.error('Import failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Import operation failed';
      // Display general error in modal
      setImportResult({
        created: 0,
        updated: 0,
        errors: [{ error: errorMessage }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={3}>
          <Iconify icon="mdi:file-import-outline" />
          <Typography variant="h6">Import Food</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Import Type Selection */}
          <FormControl component="fieldset">
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Select Import Type
            </Typography>
            <RadioGroup
              value={importType}
              onChange={(e) => setImportType(e.target.value as 'easy' | 'complex')}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: importType === 'easy' ? 2 : 1,
                      borderColor: importType === 'easy' ? 'info.main' : 'divider',
                      '&:hover': {
                        borderColor: 'info.main',
                      },
                    }}
                    onClick={() => setImportType('easy')}
                  >
                    <FormControlLabel
                      value="easy"
                      control={<Radio />}
                      label={
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify icon="mdi:lightning-bolt" width={20} color="info.main" />
                            <Typography variant="subtitle1" fontWeight={600}>
                              Easy Import
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Quick import with basic food information
                          </Typography>
                        </Stack>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: importType === 'complex' ? 2 : 1,
                      borderColor: importType === 'complex' ? 'warning.main' : 'divider',
                      '&:hover': {
                        borderColor: 'warning.main',
                      },
                    }}
                    onClick={() => setImportType('complex')}
                  >
                    <FormControlLabel
                      value="complex"
                      control={<Radio />}
                      label={
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify icon="mdi:cog" width={20} color="warning.main" />
                            <Typography variant="subtitle1" fontWeight={600}>
                              Complex Import
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Advanced import with detailed food attributes
                          </Typography>
                        </Stack>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Card>
                </Grid>
              </Grid>
            </RadioGroup>
          </FormControl>

          {/* File Upload */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select File(xlsx)
            </Typography>
            <TextField
              type="file"
              fullWidth
              onChange={handleFileChange}
              inputProps={{
                accept: '.xlsx,.xls',
              }}
            />
            {selectedFile && (
              <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>

          {/* Import Results - Show errors if any */}
          {importResult && importResult.errors && importResult.errors.length > 0 && (
            <Alert severity="error">
              <AlertTitle>
                Import Errors ({importResult.errors.length})
                {importResult.created > 0 || importResult.updated > 0 ? (
                  <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                    - {importResult.created} created, {importResult.updated} updated
                  </Typography>
                ) : null}
              </AlertTitle>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {importResult.errors.map((error, index) => (
                  <Box key={index} sx={{ mb: 1.5, pb: 1.5, borderBottom: index < importResult.errors.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    {error.sheet && (
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        Sheet: {error.sheet}
                        {error.row !== undefined && ` | Row: ${error.row}`}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {error.error}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Alert>
          )}

          {/* Helper Information */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.neutral',
              borderRadius: 1,
              border: '1px solid',
              borderColor: importType === 'easy' ? 'info.main' : 'warning.main',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {importType === 'easy' ? 'Easy Import Guide' : 'Complex Import Guide'}
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {importType === 'easy' ? (
                <>
                  <li>
                    <Typography variant="body2">
                      Upload a file with basic food information, name, description, meals, macros, availability       
                      </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Any existing food items in restaurant but not available in sheet will remain untouched
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Sets food to available for 1 week recurrsive pattern starting today.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Preview template:{' '}
                      <Button
                        size="small"
                        color="info"
                        onClick={() => handleDownloadTemplate('easy')}
                      >
                        Easy Template (Excel)
                      </Button>
                    </Typography>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <Typography variant="body2">
                      Upload a file with complete food details (excepts images)
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Any existing food items in restaurant but not available in sheet will remain untouched
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Requires proper formatting and complete data
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Preview template:{' '}
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => handleDownloadTemplate('complex')}
                      >
                        Complex Template (Excel)
                      </Button>
                    </Typography>
                  </li>
                </>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          color={importType === 'easy' ? 'info' : 'warning'}
          disabled={!selectedFile}
          startIcon={<Iconify icon="mdi:upload" />}
        >
          {importType === 'easy' ? 'Import (Easy)' : 'Import (Complex)'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
